import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';
import { COMMON_MISTAKES_EN } from '../i18n/commonMistakes';

// Labels that must read in English before the scenario translation map arrives.
export const ENGLISH_CONTENT_FALLBACKS = {
  '破冰階段': 'Ice-breaking Stage',
  '開場白': 'Opening',
  '（電話鈴聲響起...正在家中休息）': '(Phone ringing… the customer is resting at home.)',
  '（電話鈴聲響起...正在店內工作）': '(Phone ringing… the customer is working in the store.)',
  '（電話鈴聲響起...正在公司工作）': '(Phone ringing… the customer is at work.)',
  '（電話鈴聲響起...正在工作）': '(Phone ringing… the customer is at work.)',
  // Fixed common-mistake labels returned verbatim by the analysis (see i18n/commonMistakes.js)
  ...COMMON_MISTAKES_EN
};

const cacheKeyOf = (scenarioId) => `practiceLocalization:v1:${scenarioId}`;

/**
 * English display copy for a practice scenario (stage names, node names, goals, checkpoints).
 * The backend translates every visible string of the scenario once and caches it; the browser
 * keeps a localStorage copy so the practice page, flowchart and assessment report share it.
 * Scoring still uses the original Chinese strings — this only changes what is displayed.
 */
const usePracticeLocalization = (scenarioId) => {
  const { isEnglish } = useLanguage();
  const [translations, setTranslations] = useState({});
  const [isLocalizationLoading, setIsLocalizationLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!isEnglish || !scenarioId) {
      setTranslations({});
      setIsLocalizationLoading(false);
      return undefined;
    }

    const cacheKey = cacheKeyOf(scenarioId);
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
      if (cached && typeof cached === 'object') {
        setTranslations(cached);
        setIsLocalizationLoading(false);
        return undefined;
      }
    } catch (cacheError) {
      localStorage.removeItem(cacheKey);
    }

    setTranslations({});
    setIsLocalizationLoading(true);
    axios.post(`${API_URL}/api/practice-localization`, {
      scenario_id: scenarioId,
      language: 'en'
    }).then((response) => {
      if (cancelled) return;
      const next = response.data?.translations || {};
      setTranslations(next);
      try {
        localStorage.setItem(cacheKey, JSON.stringify(next));
      } catch (cacheError) {
        console.warn('Could not cache practice localization:', cacheError);
      }
    }).catch((localizationError) => {
      console.error('Error loading English practice content:', localizationError);
    }).finally(() => {
      if (!cancelled) setIsLocalizationLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [isEnglish, scenarioId]);

  const translateContent = useCallback((value) => {
    if (!isEnglish || typeof value !== 'string') return value;
    return translations[value] || ENGLISH_CONTENT_FALLBACKS[value] || value;
  }, [translations, isEnglish]);

  return { translateContent, isLocalizationLoading };
};

export default usePracticeLocalization;
